import { getApiClient } from '../axios-config';

/**
 * Employees API Service
 * Handles all API calls related to employees
 */
class EmployeesAPI {
  /**
   * Get all employees
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of employee objects
   */
  static async getAll(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/employees/');
    return response.data.data.employees;
  }

  /**
   * Get employee by ID
   * @param {string} id - Employee ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Employee object
   */
  static async getById(id, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(`/api/employees/${id}`);
    return response.data.data.employee;
  }

  /**
   * Create a new employee
   * @param {Object} employee - Employee object
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created employee object
   */
  static async create(employee, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/employees/', {
      newEmployee: employee,
    });
    return response.data.data.addedEmployee || employee;
  }

  /**
   * Update an existing employee
   * @param {Object} employee - Employee object with updates
   * @param {Object} options - Additional options (previousEmploymentStatusId, statusChangeDate, etc.)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated employee object
   */
  static async update(employee, options = {}, token) {
    const apiClient = getApiClient(token);
    const payload = {
      updatedEmployee: employee,
      ...options,
    };
    const response = await apiClient.put('/api/employees/', payload);
    return response.data.data.modifiedEmployee || employee;
  }

  /**
   * Delete an employee
   * @param {string} id - Employee ID to delete
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async delete(id, token) {
    const apiClient = getApiClient(token);
    await apiClient.delete(`/api/employees/${id}`, {
      params: {
        deletedEmployeeId: id,
      },
    });
  }

  /**
   * Delete an employee together with their user account (only allowed when
   * the user has never logged in).
   * @param {string} id - Employee ID to delete
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async deleteWithUser(id, token) {
    const apiClient = getApiClient(token);
    await apiClient.delete(`/api/employees/${id}/with-user`);
  }

  /**
   * Search employees by last name (surfaces possible duplicate matches)
   * @param {string} lastname - Last name to search for
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of matching employees
   */
  static async searchByLastname(lastname, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/employees/search', {
      params: { lastname },
    });
    return response.data.data.matches || [];
  }

  /**
   * Get employment statuses
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of employment status objects
   */
  static async getEmploymentStatuses(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/employees/employment-statuses');
    return response.data.data.employmentStatuses;
  }

  /**
   * Update employment status
   * @param {string} employeeId - Employee ID
   * @param {Object} statusData - Status change data { statusId, changedBy, notes, changedDate }
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated employee object
   */
  static async updateEmploymentStatus(employeeId, statusData, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put('/api/employees/employment-status', {
      employeeId,
      statusId: statusData.statusId,
      changedBy: statusData.changedBy,
      notes: statusData.notes,
      changedDate: statusData.changedDate,
    });
    return response.data.data.updatedEmployee;
  }

  /**
   * Get foreman assigned employees
   * @param {string} foremanId - Foreman ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of employee objects assigned to foreman
   */
  static async getForemanAssigned(foremanId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/employees/foreman-assigned', {
      params: { foremanId },
    });
    return response.data.data.employees || [];
  }

  /**
   * Get unassigned employees
   * @param {string|null|undefined} foremanId - Optional foreman ID to exclude employees already assigned to that foreman
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of unassigned employee objects
   */
  static async getUnassigned(foremanId, token) {
    const apiClient = getApiClient(token);
    const params = foremanId ? { foremanId } : {};
    const response = await apiClient.get('/api/employees/unassigned', {
      params,
    });
    return response.data.data.employees || [];
  }

  /**
   * Assign employee to crew
   * @param {string} employeeId - Employee ID
   * @param {string} foremanId - Foreman ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async assignToCrew(employeeId, foremanId, token) {
    const apiClient = getApiClient(token);
    await apiClient.post('/api/employees/assign-to-crew', {
      employeeId,
      foremanId,
    });
  }

  /**
   * Unassign employee from crew
   * @param {string} employeeId - Employee ID
   * @param {string} foremanId - Foreman ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async unassignFromCrew(employeeId, foremanId, token) {
    const apiClient = getApiClient(token);
    await apiClient.post('/api/employees/unassign-from-crew', {
      employeeId,
      foremanId,
    });
  }

  /**
   * Get employee credentials
   * @param {string} employeeId - Employee ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Credentials object with hasCredentials flag
   */
  static async getCredentials(employeeId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/employees/${employeeId}/credentials`
    );
    return response.data.data;
  }

  /**
   * Create employee credentials
   * @param {string} employeeId - Employee ID
   * @param {Object} credentials - Credentials object (email, isAdmin, isTimecard, isWorkflow, isAnalytics)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created credentials object
   */
  static async createCredentials(employeeId, credentials, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/employees/${employeeId}/credentials`,
      credentials
    );
    return response.data.data;
  }

  /**
   * Update employee credentials
   * @param {string} employeeId - Employee ID
   * @param {Object} credentials - Credentials object (email, isAdmin, isTimecard, isWorkflow, isAnalytics)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated credentials object
   */
  static async updateCredentials(employeeId, credentials, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put(
      `/api/employees/${employeeId}/credentials`,
      credentials
    );
    return response.data.data;
  }

  /**
   * Revoke employee credentials
   * @param {string} employeeId - Employee ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async revokeCredentials(employeeId, token) {
    const apiClient = getApiClient(token);
    await apiClient.put(`/api/employees/${employeeId}/credentials/revoke`, {});
  }

  /**
   * Get employee union
   * @param {string} employeeId - Employee ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Union object
   */
  static async getUnion(employeeId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(`/api/employees/${employeeId}/union`);
    // Backend returns union info directly in data, not wrapped in a union property
    return response.data.data;
  }

  /**
   * Set temporary union for employee
   * @param {string} employeeId - Employee ID
   * @param {string} union - Union name
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated employee object
   */
  static async setTemporaryUnion(employeeId, union, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put(
      `/api/employees/${employeeId}/temporary-union`,
      {
        union,
      }
    );
    // ResponseHelper.updated returns data directly, not wrapped in employee property
    return response.data.data;
  }

  /**
   * Remove temporary union for employee
   * @param {string} employeeId - Employee ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated employee object
   */
  static async removeTemporaryUnion(employeeId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.delete(
      `/api/employees/${employeeId}/temporary-union`
    );
    // ResponseHelper.success returns data directly, not wrapped in employee property
    return response.data.data;
  }

  /**
   * Upload employees CSV file
   * @param {FormData} formData - FormData containing CSV file
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Upload result
   */
  static async uploadCsv(formData, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      '/api/employees/upload-csv',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }
}

export default EmployeesAPI;
